// src/pages/JudoCoachPro/components/JudoCoachProFeatures.jsx

import React, { useState } from "react";
import {
  FaArrowRight,
  FaCheck,
  FaExternalLinkAlt,
} from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";

import {
  JUDO_COACH_CORE_MODULES,
  JUDO_COACH_FEATURE_JOURNEYS,
  JUDO_COACH_PRO,
} from "../data/judoCoachProData";

const cx = (...classes) => classes.filter(Boolean).join(" ");

function JourneyTabs({ activeJourneyId, onChange }) {
  return (
    <div
      className="inline-flex rounded-full border border-slate-200 bg-white p-1.5 shadow-sm dark:border-white/10 dark:bg-slate-900"
      role="tablist"
      aria-label="Choisir un espace Judo Coach Pro"
    >
      {JUDO_COACH_FEATURE_JOURNEYS.map((journey) => {
        const Icon = journey.icon;
        const active = journey.id === activeJourneyId;

        return (
          <button
            key={journey.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(journey.id)}
            className={cx(
              "inline-flex h-11 items-center gap-2 rounded-full px-4 text-sm font-semibold transition sm:px-5",
              active
                ? "bg-[#0868f2] text-white shadow-sm"
                : "text-slate-500 hover:text-slate-950 dark:text-slate-400 dark:hover:text-white",
            )}
          >
            <Icon className={active ? "text-[#ffb067]" : "text-[#0868f2]"} />
            {journey.tabLabel}
          </button>
        );
      })}
    </div>
  );
}

function FeatureItem({ feature, index }) {
  const Icon = feature.icon;

  return (
    <article
      className={cx(
        "py-5",
        index > 0 && "border-t border-slate-200 dark:border-white/10",
      )}
    >
      <div className="flex items-start gap-4">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-blue-50 text-[#0868f2] dark:bg-blue-500/10 dark:text-blue-300">
          <Icon />
        </span>

        <div>
          <h4 className="text-base font-semibold text-slate-950 dark:text-white">
            {feature.title}
          </h4>

          <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
            {feature.text}
          </p>
        </div>
      </div>
    </article>
  );
}

function ProductVisual({ journey }) {
  return (
    <div className="relative">
      <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white p-2 shadow-[0_30px_90px_-50px_rgba(15,23,42,0.5)] dark:border-white/10 dark:bg-slate-900">
        <div className="relative aspect-[16/11] overflow-hidden rounded-[18px] bg-slate-100 dark:bg-slate-950">
          <img
            key={journey.image}
            src={journey.image}
            alt={journey.imageAlt}
            className="h-full w-full object-cover"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-transparent to-transparent" />

          <div className="absolute left-4 top-4">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-slate-950/45 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white backdrop-blur-md">
              <FaCheck className="text-[#ff7900]" />
              {journey.accentLabel}
            </span>
          </div>
        </div>
      </div>

      <div className="relative -mt-8 ml-auto mr-5 w-[58%] max-w-[310px] overflow-hidden rounded-[18px] border-4 border-white bg-white shadow-2xl dark:border-slate-900 dark:bg-slate-900 sm:-mt-14">
        <div className="aspect-[4/3] overflow-hidden">
          <img
            key={journey.secondaryImage}
            src={journey.secondaryImage}
            alt={journey.secondaryLabel}
            className="h-full w-full object-cover"
          />
        </div>

        <div className="border-t border-slate-100 px-4 py-3 dark:border-white/10">
          <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            {journey.secondaryLabel}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function JudoCoachProFeatures() {
  const [activeJourneyId, setActiveJourneyId] = useState("judoka");

  const activeJourney =
    JUDO_COACH_FEATURE_JOURNEYS.find(
      (journey) => journey.id === activeJourneyId,
    ) || JUDO_COACH_FEATURE_JOURNEYS[0];

  return (
    <section
      id="jcp-features"
      className="scroll-mt-24 overflow-hidden bg-[#f3f6f5] py-20 dark:bg-slate-900 sm:py-24 lg:py-28"
    >
      <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="grid gap-8 border-b border-slate-300/70 pb-10 dark:border-white/10 lg:grid-cols-[1fr_auto] lg:items-end">
            <div className="max-w-4xl">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0868f2] dark:text-blue-300">
                Deux expériences complémentaires
              </p>

              <h2 className="mt-5 text-4xl font-semibold leading-[1.08] tracking-[-0.04em] text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
                Pensé pour apprendre,
                <span className="block text-[#0868f2] dark:text-blue-300">
                  conçu pour transmettre.
                </span>
              </h2>

              <p className="mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
                Chaque utilisateur retrouve uniquement les outils utiles à son
                rôle, dans une interface simple et accessible.
              </p>
            </div>

            <JourneyTabs
              activeJourneyId={activeJourneyId}
              onChange={setActiveJourneyId}
            />
          </div>
        </Reveal>

        <div
          key={activeJourney.id}
          className="mt-12 grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center xl:gap-16"
        >
          <Reveal>
            <ProductVisual journey={activeJourney} />
          </Reveal>

          <Reveal>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#ff7900]">
                {activeJourney.eyebrow}
              </p>

              <h3 className="mt-4 max-w-xl text-3xl font-semibold leading-tight tracking-[-0.035em] text-slate-950 dark:text-white sm:text-4xl">
                {activeJourney.title}
              </h3>

              <p className="mt-5 max-w-xl text-base leading-8 text-slate-600 dark:text-slate-300">
                {activeJourney.description}
              </p>

              <div className="mt-7 border-y border-slate-300/70 dark:border-white/10">
                {activeJourney.features.map((feature, index) => (
                  <FeatureItem
                    key={feature.title}
                    feature={feature}
                    index={index}
                  />
                ))}
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={JUDO_COACH_PRO.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex h-12 items-center justify-center gap-3 rounded-full bg-[#0868f2] px-6 text-sm font-semibold text-white transition hover:bg-[#0648b8]"
                >
                  Tester Judo Coach Pro

                  <span className="grid h-7 w-7 place-items-center rounded-full bg-[#ff7900]">
                    <FaExternalLinkAlt className="text-[9px]" />
                  </span>
                </a>

                <a
                  href="#jcp-pricing"
                  className="group inline-flex h-12 items-center justify-center gap-3 rounded-full border border-slate-300 px-6 text-sm font-semibold text-slate-800 transition hover:border-[#0868f2] hover:text-[#0868f2] dark:border-white/15 dark:text-white dark:hover:border-blue-300 dark:hover:text-blue-300"
                >
                  Voir les formules

                  <FaArrowRight className="text-[10px] transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-20">
          <div className="border-y border-slate-300/70 py-7 dark:border-white/10">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
              Les principaux modules
            </p>

            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-3">
              {JUDO_COACH_CORE_MODULES.map((module, index) => (
                <React.Fragment key={module}>
                  <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    {module}
                  </span>

                  {index < JUDO_COACH_CORE_MODULES.length - 1 ? (
                    <span className="hidden text-[#ff7900] sm:inline">
                      •
                    </span>
                  ) : null}
                </React.Fragment>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}